import { useEffect, useState } from 'react';
import LoadingIndicator from 'components/LoadingIndicator';
import { IonCol, IonGrid, IonRow } from '@ionic/react';
import Title from 'components/Title';
import useResponsive from 'features/responsive/responsive';
import { useTranslation } from 'react-i18next';
import useAds from 'hooks/useAds';
import AdComponent from 'components/Ads/AdComponent';
import WavePatternContainer from 'components/WavePatternContainer';
import TopicCard from 'components/Cards/TopicCard';
import PageLayout from '../components/PageLayout';
import AdRightBlock from '../components/Ads/AdRightBlock';
import PageNoData from './PageNoData';

interface CategoryItem {
    pictureUrl: string;
    name: string;
    id: string;
    routerLink: string;
    active: boolean;
}

interface CategoryConfig {
    title: string;
    type: string;
    items: CategoryItem[];
}

interface CategoriesConfig {
    categories: Record<string, CategoryConfig>;
}

export default function CategoryPage() {
    const { IsDesktop } = useResponsive();
    const { t } = useTranslation();
    const lastPathSegment = window.location.pathname.split('/').filter(Boolean).pop();
    const { ads } = useAds({ page: 'category-news-replays' });
    
    const [categoryData, setCategoryData] = useState<Record<string, CategoryConfig>>({});
    const [configLoading, setConfigLoading] = useState(true);
    const [configError, setConfigError] = useState<string | null>(null);

    // Load category config from JSON file
    useEffect(() => {
        const loadCategoryConfig = async () => {
            try {
                setConfigLoading(true);
                setConfigError(null);
                
                const apiUrl = process.env.REACT_APP_API_URL || '';
                if (!apiUrl) {
                    throw new Error('API URL is not configured');
                }
                
                const response = await fetch(`${apiUrl}/config/category.json`);
                if (!response.ok) {
                    throw new Error(`Failed to load category config: ${response.status}`);
                }
                
                const data: CategoriesConfig = await response.json();
                setCategoryData(data.categories);
            } catch (error) {
                console.error('Error loading category config:', error);
                setConfigError(error instanceof Error ? error.message : 'Failed to load configuration');
            } finally {
                setConfigLoading(false);
            }
        };

        loadCategoryConfig();
    }, []);

    function generateTopicCards(category: string | undefined) {
        // Check if the category exists in categoryData
        if (category && category in categoryData) {
            return (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8 h-fit">
                    {categoryData[category].items.filter(item => item?.active).map(
                        (
                            item,
                            index,
                        ) => (
                            <TopicCard
                                key={index}
                                item={item}
                                routerLink={item.routerLink}
                                imgProps={{
                                    className: 'h-[125px] grow',
                                }}
                            />
                        ),
                    )}
                </div>
            );
        }
        return null;
    }

    const titleOfPage = categoryData[lastPathSegment as keyof typeof categoryData]?.title;

    if (configLoading) {
        return (
            <PageLayout title={t(titleOfPage) ?? ''}>
                <LoadingIndicator />
            </PageLayout>
        );
    }

    // Use ternary operator to show PageNoData if config fails to load
    return configError ? (
        <PageNoData />
    ) : (
        <PageLayout title={t(titleOfPage) ?? ''}>
            <IonGrid
                fixed
                className="min-h-[50%]"
            >
                {ads && <AdComponent ad={ads.data[0]} />}
                <WavePatternContainer
                    autoMargin
                    fixed="page"
                    reverse
                    position="top-right"
                    className="mb-0"
                    component="section"
                >
                    {IsDesktop ? (
                        <IonRow className="mb-blockInnerMobile md:mb-blockInner">
                            <IonCol size-xs={12}>
                                <Title
                                    main
                                    title={t(titleOfPage) ?? ''}
                                />
                            </IonCol>
                        </IonRow>
                    ) : (
                        ''
                    )}
                </WavePatternContainer>
                <AdRightBlock ad={ads?.data?.[1]}>{generateTopicCards(lastPathSegment)}</AdRightBlock>
                {ads && (
                    <AdComponent
                        ad={ads.data[2]}
                        className="mt-[50px]"
                    />
                )}
            </IonGrid>
        </PageLayout>
    );
}
